import styled from 'styled-components';

import { IconBuilder } from '@components/icons/builder';
import { IconCheckmark } from '@components/icons/checkmark';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { LoadingLabel } from './hooks';

type LoadingStateDisplayProps = {
  loadingLabel: LoadingLabel | null;
  isBuilding: boolean;
};

export const LoadingStateDisplay = ({ loadingLabel, isBuilding }: LoadingStateDisplayProps) => {
  if (!loadingLabel) {
    return null;
  }

  if (loadingLabel === 'Building workflow') {
    return <BuildingWorkflowState isBuilding={isBuilding} />;
  }

  return <DefaultLoadingState label={loadingLabel} />;
};

const BuildingWorkflowState = ({ isBuilding }: { isBuilding: boolean }) => {
  const { t } = useScopedTranslate(locale);

  return (
    <StatusRow>
      <ToolMessageContainer>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" justify="center" align="center" space={2}>
            <IconBuilder color="elementPurple80" />
            <Text font="semibold12" text={t('buildingWorkflow')} />
          </Stack>
          {isBuilding ? (
            <SizedRelativeSpinner size={16} $skipMargin />
          ) : (
            <IconCheckmark color="elementGreen100" />
          )}
        </Stack>
      </ToolMessageContainer>
    </StatusRow>
  );
};

const DefaultLoadingState = ({ label }: { label: string }) => {
  return (
    <StatusRow>
      <AssistantBorder>
        <AssistantMessageContainer>
          <Stack orientation="horizontal" justify="center" align="center" space={2}>
            <SizedRelativeSpinner size={24} $skipMargin />
            <Text aiGradient font="regular14" text={label} />
          </Stack>
        </AssistantMessageContainer>
      </AssistantBorder>
    </StatusRow>
  );
};

const StatusRow = styled.div({
  padding: `${pxToRem(8)} 0`,
});

const AssistantBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: 'linear-gradient(112deg, rgba(99, 214, 250, 0.20) 0%, rgba(224, 57, 206, 0.20) 100%)',
});

const AssistantMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const ToolMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: 'transparent',
  border: `1px solid ${theme.color.border10}`,
});

const locale = {
  en: {
    buildingWorkflow: 'Building workflow',
  },
};
